import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { DollarSign, CheckCircle, Search, X } from 'lucide-react';
import { toast } from 'sonner';

const AccountsReceivable = () => {
  const [pendingSales, setPendingSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('dinheiro');

  // Novos estados para os filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchPendingSales();
  }, []);

  const fetchPendingSales = async () => {
    try {
      const response = await api.sales.getPending();
      setPendingSales(response.data);
    } catch (error) {
      toast.error('Erro ao carregar contas pendentes');
    } finally {
      setLoading(false);
    }
  };

  const openPaymentDialog = (sale) => {
    setSelectedSale(sale);
    setPaymentAmount(sale.total.toString());
    setPaymentDialogOpen(true);
  };

  const handlePayment = async () => {
    try {
      await api.payments.create({
        sale_id: selectedSale.id,
        amount: parseFloat(paymentAmount),
        payment_method: paymentMethod
      });
      toast.success('Pagamento registrado!');
      setPaymentDialogOpen(false);
      fetchPendingSales();
    } catch (error) {
      toast.error('Erro ao registrar pagamento');
    }
  };

  // Motor de Filtragem
  const filteredSales = pendingSales.filter(sale => {
    const matchesSearch = sale.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
    
    let matchesStart = true;
    let matchesEnd = true;
    
    if (startDate || endDate) {
      // Extrai apenas o YYYY-MM-DD da data ISO do banco
      const saleDateStr = sale.created_at.split('T')[0]; 
      
      if (startDate) {
        matchesStart = saleDateStr >= startDate;
      }
      if (endDate) {
        matchesEnd = saleDateStr <= endDate;
      }
    }
    
    return matchesSearch && matchesStart && matchesEnd;
  });

  // O total agora reflete o que está filtrado na tela
  const totalPending = filteredSales.reduce((sum, sale) => sum + sale.total, 0);

  const clearFilters = () => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground font-mono">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accounts-receivable-page">
      <div className="flex items-center justify-between">
        <h1 className="text-5xl font-bold uppercase tracking-tight">Contas a Receber</h1>
        <Card className="bg-primary/10 border-primary px-6 py-4">
          <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
            {searchTerm || startDate || endDate ? 'Total Filtrado' : 'Total Pendente'}
          </p>
          <p className="text-3xl font-mono font-bold text-primary" data-testid="total-pending">R$ {totalPending.toFixed(2)}</p>
        </Card>
      </div>

      {/* BARRA DE FILTROS */}
      <Card className="bg-card border-border mb-6">
        <CardContent className="p-4 flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1 w-full">
            <Label className="text-xs uppercase tracking-wider mb-1 block">Buscar Cliente</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="Digite o nome..." 
                className="pl-9 font-mono"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="w-full md:w-auto">
             <Label className="text-xs uppercase tracking-wider mb-1 block">Data Inicial</Label>
             <Input 
               type="date" 
               className="font-mono"
               value={startDate}
               onChange={(e) => setStartDate(e.target.value)}
             />
          </div>
          <div className="w-full md:w-auto">
             <Label className="text-xs uppercase tracking-wider mb-1 block">Data Final</Label>
             <Input 
               type="date" 
               className="font-mono"
               value={endDate}
               onChange={(e) => setEndDate(e.target.value)}
             />
          </div>
          {(searchTerm || startDate || endDate) && (
            <Button 
              variant="ghost" 
              className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors uppercase font-bold text-xs h-10"
              onClick={clearFilters}
            >
              <X className="h-4 w-4 mr-2" /> Limpar
            </Button>
          )}
        </CardContent>
      </Card>

      <Card className="bg-card border-border" data-testid="pending-sales-list">
        <CardContent className="p-0">
          {filteredSales.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="h-12 w-12 text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Nenhuma conta pendente encontrada.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground font-mono uppercase text-xs">Data</TableHead>
                  <TableHead className="text-muted-foreground font-mono uppercase text-xs">Cliente</TableHead>
                  <TableHead className="text-muted-foreground font-mono uppercase text-xs">Itens</TableHead>
                  <TableHead className="text-muted-foreground font-mono uppercase text-xs">Forma Pagamento</TableHead>
                  <TableHead className="text-muted-foreground font-mono uppercase text-xs text-right">Valor</TableHead>
                  <TableHead className="text-muted-foreground font-mono uppercase text-xs text-right">Ação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSales.map((sale) => (
                  <TableRow key={sale.id} className="border-border/50 hover:bg-secondary/50" data-testid={`pending-sale-${sale.id}`}>
                    <TableCell className="font-mono text-sm">
                      {new Date(sale.created_at).toLocaleDateString('pt-BR')}
                    </TableCell>
                    <TableCell className="font-medium">{sale.customer_name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {sale.items.map(item => `${item.quantity}x ${item.product_name}`).join(', ')}
                    </TableCell>
                    <TableCell className="uppercase text-xs font-mono">{sale.payment_method}</TableCell>
                    <TableCell className="font-mono font-bold text-destructive text-right">
                      R$ {sale.total.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        onClick={() => openPaymentDialog(sale)}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase text-xs"
                        data-testid={`pay-sale-${sale.id}`}
                      >
                        <DollarSign className="mr-1 h-4 w-4" /> Receber
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="bg-card border-border" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl uppercase font-bold">Registrar Pagamento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-secondary border border-border">
              <p className="text-sm text-muted-foreground">Cliente</p>
              <p className="font-bold text-lg">{selectedSale?.customer_name}</p>
              <p className="text-sm text-muted-foreground mt-2">Total da Venda</p>
              <p className="font-mono font-bold text-2xl text-primary">R$ {selectedSale?.total.toFixed(2)}</p>
            </div>
            
            <div>
              <Label className="text-xs uppercase tracking-wider">Valor Recebido</Label>
              <Input
                type="number"
                step="0.01"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                className="mt-1 font-mono text-lg"
                data-testid="payment-amount-input"
              />
            </div>
            
            <Button
              onClick={handlePayment}
              className="w-full bg-primary hover:bg-primary/90 font-bold uppercase h-12"
              data-testid="confirm-payment-button"
            >
              Confirmar Pagamento
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountsReceivable;
